/**
 * menu.js
 *
 * @version 1.0.0
 * @author …
 * @license …
 */

/* ----------------------------------------------------------------------- */
/*  DOM constants                                                          */
/* ----------------------------------------------------------------------- */

const ATTR_TARGET = 'data-menu-target';
const ATTR_MENU = 'data-menu';
const ATTR_CLOSE = 'data-menu-close';
const ATTR_CLOSING = 'data-closing';

/* ----------------------------------------------------------------------- */
/*  Utilities                                                              */
/* ----------------------------------------------------------------------- */

/**
 * Close every open menu except the one passed in (if any).
 * @param {HTMLElement} [except] – the menu to leave open
 */
function closeOtherMenus(except) {
	document.querySelectorAll('[data-menu][open]').forEach(menu => {
		if (menu !== except) closeMenu(menu, 'dismiss');
	});
}

/**
 * Return the total transition time (delay + duration) in ms.
 * If there is no transition, the result is 0.
 * @param {HTMLElement} el
 */
function getTransitionTime(el) {
	const style = getComputedStyle(el);
	const durRaw = style.transitionDuration.split(',')[0].trim();  // first value only
	const delRaw = style.transitionDelay.split(',')[0].trim();
	const dur = parseFloat(durRaw);
	const del = parseFloat(delRaw);
	const msDur = durRaw.endsWith('ms') ? dur : dur * 1000;
	const msDelay = delRaw.endsWith('ms') ? del : del * 1000;
	return msDur + msDelay;
}

/* ----------------------------------------------------------------------- */
/*  Event handlers                                                         */
/* ----------------------------------------------------------------------- */

function handleToggle(event) {
	const trigger = event.target.closest(`[${ATTR_TARGET}]`);

	if (trigger) {
		const name = trigger.getAttribute(ATTR_TARGET);
		const selector = `[${ATTR_MENU}="${CSS.escape(name)}"]`;
		const menu = document.querySelector(selector);
		if (!menu) return;

		menu.hasAttribute('open')
			? closeMenu(menu) // toggle closed
			: (closeOtherMenus(), openMenu(trigger, menu)); // open exclusively

		return;
	}

	const closeBtn = event.target.closest(`[${ATTR_CLOSE}]`);
	if (closeBtn) {
		const menu = closeBtn.closest('[data-menu]');
		if (menu) closeMenu(menu);
		return;
	}

	const anyMenu = document.querySelector('[data-menu][open]');
	const clickAway = anyMenu && !anyMenu.contains(event.target);
	if (clickAway) closeMenu(anyMenu, 'dismiss');
}

function handleEscape(event) {
	if (event.key !== 'Escape') return;
	const openMenu = document.querySelector('[data-menu][open]');
	if (openMenu) closeMenu(openMenu, 'dismiss');
}

/* ----------------------------------------------------------------------- */
/*  Core helpers                                                           */
/* ----------------------------------------------------------------------- */

function openMenu(trigger, menu) {
	closeOtherMenus(menu); // always keep it exclusive

	menu.hidden = false; // reveal first so transition can run
	menu.setAttribute('open', '');
	menu.removeAttribute('inert');

	trigger.setAttribute('aria-expanded', 'true');

	const firstItem = menu.querySelector('[role="menuitem"]');
	(firstItem || menu).focus({ preventScroll: true });
}

function closeMenu(menu) {
	if (menu.hasAttribute(ATTR_CLOSING) || !menu.hasAttribute('open')) return;

	menu.setAttribute(ATTR_CLOSING, '');

	const finish = () => {
		menu.removeAttribute(ATTR_CLOSING);
		menu.removeAttribute('open');
		menu.hidden = true;
		menu.setAttribute('inert', '');

		const id = menu.getAttribute(ATTR_MENU);
		const trigger = document.querySelector(`[${ATTR_TARGET}="${CSS.escape(id)}"]`);
		if (trigger) trigger.setAttribute('aria-expanded', 'false');
	};

	const totalMs = getTransitionTime(menu);

	if (totalMs === 0) {
		finish();
	} else {
		const timeoutId = setTimeout(finish, totalMs + 50); // failsafe
		menu.addEventListener(
			'transitionend',
			() => {
				clearTimeout(timeoutId);
				finish();
			},
			{ once: true }
		);
	}
}

/* ----------------------------------------------------------------------- */
/*  Public API                                                             */
/* ----------------------------------------------------------------------- */

export function initMenus() {
	// Ensure the page never starts with more than one open
	closeOtherMenus(document.querySelector('[data-menu][open]'));

	document.addEventListener('click', handleToggle, false);
	document.addEventListener('keydown', handleEscape, false);

	return () => {
		document.removeEventListener('click', handleToggle, false);
		document.removeEventListener('keydown', handleEscape, false);
	};
}
